import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateActivimeterCalibrationDto,
  ActivimeterCalibrationDto,
  HotLabEquipmentDto,
  HotLabEquipmentType,
  IsotopeDto,
} from '@services/dtos'
import { activimeterCalibrationSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid, Typography } from '@mui/material'
import {
  createActivimeterCalibration,
  updateActivimeterCalibration,
} from '@state/reducers/activimeterCalibrationReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import { FormCrudHeader } from './FormCrudHeader'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import HotLabEquipmentForm from './HotLabEquipmentForm'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import { CheckBoxInput } from '../inputs'
import IsotopeForm from './IsotopeForm'
import { isotopeService } from '@services/IsotopeService'

type Props = {
  editingActivimeterCalibration?: ActivimeterCalibrationDto | null
  onClose: () => void
}

const ActivimeterCalibrationForm: React.FC<Props> = ({
  editingActivimeterCalibration,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateActivimeterCalibrationDto>({
    defaultValues: editingActivimeterCalibration
      ? {
          isotopeId: editingActivimeterCalibration.isotope.id,
          equipmentId: editingActivimeterCalibration.equipment.id,
          value: editingActivimeterCalibration.value,
          forbidElutionCreation:
            editingActivimeterCalibration.forbidElutionCreation,
          forbidMrpCreation: editingActivimeterCalibration.forbidMrpCreation,
          forbidSyringeCreation:
            editingActivimeterCalibration.forbidSyringeCreation,
          forbidSingleDoseCreation:
            editingActivimeterCalibration.forbidSingleDoseCreation,
          forbidInhalationCreation:
            editingActivimeterCalibration.forbidInhalationCreation,
        }
      : {},
    resolver: yupResolver(activimeterCalibrationSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateActivimeterCalibrationDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () => cb('Une erreur est survenue, vérifiez que le combo activimètre/isotope n\'existe pas déjà')

    if (editingActivimeterCalibration) {
      dispatch(
        updateActivimeterCalibration({
          id: editingActivimeterCalibration.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('Le calibrage activimètre a été modifié avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createActivimeterCalibration(data))
        .unwrap()
        .then(() => {
          cb('Le calibrage activimètre a été créé avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields1 = [{ name: 'value', label: 'Calibre', sm: '12' }]
  const fields2 = [
    {
      name: 'forbidElutionCreation',
      label: 'Elution',
      sm: '4',
      type: 'checkbox',
    },
    { name: 'forbidMrpCreation', label: 'MRP', sm: '4', type: 'checkbox' },
    {
      name: 'forbidSyringeCreation',
      label: 'Seringue',
      sm: '4',
      type: 'checkbox',
    },
    {
      name: 'forbidSingleDoseCreation',
      label: 'Dose unitaire',
      sm: '6',
      type: 'checkbox',
    },
    {
      name: 'forbidInhalationCreation',
      label: 'Inhalation',
      sm: '6',
      type: 'checkbox',
    },
  ]

  const fetchActivimeters = async (search: string) => {
    const page = await hotLabEquipmentService.getAll({
      search,
      type: HotLabEquipmentType.ACTIVIMETER,
    })
    return page?.datas ?? []
  }
  const fetchIsotopes = async (search: string) => {
    const page = await isotopeService.getAll({ search })
    return page?.datas ?? []
  }

  const renderFields = (fields: any[]) =>
    fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        {field.type === 'checkbox' ? (
          <CheckBoxInput
            control={control}
            name={field.name}
            label={field.label}
          />
        ) : (
          <InputField
            control={control}
            name={field.name}
            label={field.label}
            type={field.type ?? 'text'}
          />
        )}
      </Grid>
    ))

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingActivimeterCalibration}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Calibrage activimètre
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Activimètre"
                  addTooltipTitle="Ajouter un activimètre"
                  dialogContent={(close) => (
                    <HotLabEquipmentForm
                      onClose={close}
                      defaultValues={{ type: HotLabEquipmentType.ACTIVIMETER }}
                    />
                  )}
                  input={
                    <ServerAutocomplete<HotLabEquipmentDto>
                      autoFetchOnMount
                      fetchService={fetchActivimeters}
                      onChange={(value: HotLabEquipmentDto | null) => {
                        setValue('equipmentId', value?.id ?? '')
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const equipment = value as HotLabEquipmentDto
                        return equipment ? equipment.name : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un activimètre"
                      label="Activimètre"
                      helperText={errors.equipmentId?.message}
                      error={!!errors.equipmentId}
                      minSearchLength={0}
                      initialOptions={
                        editingActivimeterCalibration?.equipment
                          ? [editingActivimeterCalibration.equipment]
                          : []
                      }
                      value={editingActivimeterCalibration?.equipment ?? null}
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Isotope"
                  addTooltipTitle="Ajouter un isotope"
                  dialogContent={(close) => <IsotopeForm onClose={close} />}
                  input={
                    <ServerAutocomplete<IsotopeDto>
                      fetchService={fetchIsotopes}
                      onChange={(value: IsotopeDto | null) => {
                        setValue('isotopeId', value?.id ?? '')
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const isotope = value as IsotopeDto
                        return isotope ? isotope.name : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un isotope"
                      label="Isotope"
                      helperText={errors.isotopeId?.message}
                      error={!!errors.isotopeId}
                      minSearchLength={0}
                      initialOptions={
                        editingActivimeterCalibration?.isotope
                          ? [editingActivimeterCalibration.isotope]
                          : []
                      }
                      value={editingActivimeterCalibration?.isotope ?? null}
                    />
                  }
                />
              </Grid>

              {renderFields(fields1)}

              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Interdictions de création
                </Typography>
              </Grid>

              {renderFields(fields2)}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default ActivimeterCalibrationForm
